import React, { useState } from 'react';
import { FaTimes, FaExclamationTriangle, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

const BloodTestWarningModal = ({ isOpen, warningMessage, onClose, onForceCreate }) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleForceCreateClick = () => {
    setShowPasswordModal(true);
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setPasswordError('');

    try {
      // Call the force create handler with password
      onForceCreate(password);
      
      // Close both modals immediately
      setShowPasswordModal(false);
      setPassword('');
      onClose(); // Close the main warning modal
    } catch (error) {
      setPasswordError('An error occurred. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Warning Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b bg-yellow-50">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="text-yellow-600" size={24} />
              <h2 className="text-2xl font-bold text-gray-800">Biological Age Warning</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-700 mb-6">
              A biological age discrepancy has been detected. Admin authorization is required to proceed with the blood test report generation.
            </p>

            {/* Warning Box */}
            <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded mb-6">
              <div className="font-semibold text-yellow-800 mb-1">
                Biological Age Difference
              </div>
              <div className="text-yellow-700 text-sm">
                {warningMessage}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> This warning indicates a significant difference between the patient's chronological and biological age. 
                Only administrators with proper authorization can override this warning and proceed with the report generation.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleForceCreateClick}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
            >
              <FaLock size={16} />
              Force Create (Admin)
            </button>
          </div>
        </div>
      </div>

      {/* Password Verification Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b bg-orange-50">
              <div className="flex items-center gap-3">
                <FaLock className="text-orange-600" size={20} />
                <h2 className="text-xl font-bold text-gray-800">Admin Authorization</h2>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword('');
                  setPasswordError('');
                }}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handlePasswordSubmit} className="p-6">
              <p className="text-gray-700 mb-4">
                Enter the admin password to authorize this blood test report generation.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError('');
                    }}
                    placeholder="Enter password"
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                      passwordError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={verifying}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    disabled={verifying}
                    tabIndex="-1"
                  >
                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-600 text-sm mt-2">{passwordError}</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setPasswordError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={verifying}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={verifying || !password}
                >
                  {verifying ? 'Verifying...' : 'Verify & Proceed'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BloodTestWarningModal;
